/**
 * Cómo se llama cada pantalla del panel para alguien que la usa, no para quien la programó.
 *
 * Existe porque no había ningún mapa así: el `data` de las rutas solo lleva el RBAC
 * (`{ module, permission }`), no hay servicio de breadcrumbs, y `MENU_AUTHORIZATION` únicamente
 * cubre las entradas del menú lateral — no las pantallas de detalle, que es donde más se trabaja.
 *
 * Se usa para dos cosas: componer el título automático de un reporte de bug ("Pagos: no se guardó")
 * y agrupar los reportes por pantalla.
 */
export interface ScreenLabel {
  /** Patrón con segmentos `:param`, comparado segmento a segmento contra el path sin query. */
  pattern: string
  label: string
}

//! El ORDEN importa: las rutas literales van ANTES que las paramétricas, o `/quote/parameters/:id`
//! haría match con `/quote/:id` y toda propuesta de cotización se llamaría "Detalle de una
//! cotización". Es la misma trampa que el propio `app-routing.module.ts` ya documenta con `new`
//! antes de `:id`.
export const SCREEN_LABELS: ScreenLabel[] = [
  { pattern: '/', label: 'Inicio' },

  { pattern: '/active', label: 'Activos' },
  { pattern: '/active/:id', label: 'Ficha de un activo' },

  { pattern: '/customer', label: 'Clientes' },
  { pattern: '/customer/:id', label: 'Expediente de un cliente' },

  { pattern: '/quote', label: 'Cotizaciones' },
  { pattern: '/quote/parameters/:id', label: 'Propuesta de cotización' },
  { pattern: '/quote/:id', label: 'Detalle de una cotización' },

  { pattern: '/lease', label: 'Arrendamientos' },
  { pattern: '/lease/:id', label: 'Expediente de un arrendamiento' },

  //! Cada vista de Cobranza tiene su NOMBRE, no una etiqueta común. Antes las tres puertas resolvían
  //! a «Cobranza» a secas y todo reporte de bug nacía sin decir desde cuál de las cinco vistas salió;
  //! ahora la vista vive en la ruta y el nombre puede ser preciso. El orden importa: las literales
  //! antes que las paramétricas, o `/cobranza/lote/:uploadBatch` no llegaría a compararse.
  { pattern: '/cobranza/dinero', label: 'Cobranza · Dinero' },
  { pattern: '/cobranza/comprobantes', label: 'Cobranza · Comprobantes' },
  { pattern: '/cobranza/central', label: 'Cobranza · CENTRAL' },
  { pattern: '/cobranza/cartera', label: 'Cobranza · Cartera' },
  { pattern: '/cobranza/avance', label: 'Cobranza · Avance' },
  { pattern: '/cobranza/conciliacion', label: 'Cobranza · Conciliación' },
  { pattern: '/cobranza/por-atender', label: 'Cobranza · Por atender' },
  { pattern: '/cobranza/lote', label: 'Carga de pagos' },
  { pattern: '/cobranza/lote/:uploadBatch', label: 'Carga de pagos' },
  { pattern: '/cobranza', label: 'Cobranza' },

  //* Comités (F-143): cada vista con su nombre, y `sesion/:id` porque es el destino del correo. La
  //* literal `/comites` va al final, después de sus hijas, igual que `/cobranza`.
  { pattern: '/comites/sesiones', label: 'Comités · Sesiones' },
  { pattern: '/comites/por-originar', label: 'Comités · Por originar' },
  { pattern: '/comites/activos', label: 'Comités · Activos' },
  { pattern: '/comites/cerrados', label: 'Comités · Cerrados' },
  { pattern: '/comites/sesion/:id', label: 'Comités · Sesión' },
  { pattern: '/comites', label: 'Comités' },
  { pattern: '/enforceability', label: 'Exigibilidad' },
  { pattern: '/funnel-tasks', label: 'Bandeja de funnel' },

  { pattern: '/admin/reports', label: 'Reportería' },
  { pattern: '/invoice-rules', label: 'Reglas de facturación' },
  { pattern: '/admin', label: 'Administradores' },

  { pattern: '/wallet', label: 'Cartera' },
  { pattern: '/wallet/admin-details', label: 'Cartera (detalle administrativo)' },
  { pattern: '/wallet/:id', label: 'Detalle de cartera' },

  { pattern: '/algorithm-config', label: 'Algoritmo de ofertas' },
  { pattern: '/algorithm-config/new', label: 'Algoritmo de ofertas (nuevo)' },
  { pattern: '/algorithm-config/:id', label: 'Algoritmo de ofertas (detalle)' },

  { pattern: '/risk-scorecard-config', label: 'Calculadora de riesgo (configuración)' },
  { pattern: '/risk-scorecard-config/new', label: 'Calculadora de riesgo (versión nueva)' },
  { pattern: '/risk-scorecard-config/:id', label: 'Calculadora de riesgo (versión)' },

  { pattern: '/credit-history/validations', label: 'Validaciones de buró' },
  { pattern: '/credit-history/validations/:id', label: 'Validación de buró (detalle)' },

  { pattern: '/preofertas/legal/:id', label: 'Preoferta por autorizar' },
  { pattern: '/preofertas/configuracion', label: 'Preoferta automática (configuración)' },
  { pattern: '/preofertas/legal', label: 'Preofertas por autorizar' },

  { pattern: '/wallet-config', label: 'Configuración de cartera' },
  { pattern: '/wallet-config/:id', label: 'Configuración de cartera (detalle)' },

  { pattern: '/dripping/templates', label: 'Plantillas de cobranza' },
  { pattern: '/dripping/templates/:id', label: 'Plantilla de cobranza' },
  { pattern: '/dripping/strategies', label: 'Estrategias de cobranza' },
  { pattern: '/dripping/strategies/new', label: 'Estrategia de cobranza (nueva)' },
  { pattern: '/dripping/strategies/:id/edit', label: 'Estrategia de cobranza (edición)' },
  { pattern: '/dripping/strategies/:id', label: 'Estrategia de cobranza' },
  { pattern: '/dripping/campaigns', label: 'Campañas de cobranza' },
  { pattern: '/dripping/campaigns/:id', label: 'Campaña de cobranza' },
  { pattern: '/dripping/communications', label: 'Comunicaciones enviadas' },

  { pattern: '/provider', label: 'Proveedores' },
  { pattern: '/provider/:id', label: 'Ficha de un proveedor' },

  { pattern: '/documentation', label: 'Manuales de la plataforma' },
  { pattern: '/documentation/avance', label: 'Avance de la revisión' },
  { pattern: '/documentation/:slug', label: 'Manual de la plataforma' },

  { pattern: '/roadmap', label: 'Mapa de trabajo' },
  { pattern: '/roadmap/proyecto/:slug', label: 'Ficha de un proyecto' },

  { pattern: '/omnichannel', label: 'Canales' },
  { pattern: '/omnichannel/metrics', label: 'Métricas de omnicanal' },

  //* Sin shell: aquí no se monta el botón de reportar. Están para que la bitácora sepa nombrarlas
  //* si algo pasa antes de entrar.
  { pattern: '/login', label: 'Inicio de sesión' },
  { pattern: '/change-password', label: 'Cambio de contraseña' },
  { pattern: '/quoter', label: 'Cotizador público' },
  { pattern: '/logout', label: 'Cierre de sesión' },
]

//* Cuando la ruta no está en el mapa. En español y en minúscula porque se lee dentro de una frase.
export const UNKNOWN_SCREEN_LABEL = 'la plataforma'

function segmentsOf(path: string): string[] {
  return path.split('/').filter((s) => s.length > 0)
}

/**
 * Resuelve una URL al patrón de su pantalla y a su nombre legible.
 *
 * Compara segmento a segmento con `:param` como comodín, y devuelve la PRIMERA coincidencia — por
 * eso el orden del arreglo es parte del contrato. Si no hay ninguna, devuelve la ruta tal cual con
 * el nombre genérico: un título feo es mejor que un reporte que no se puede ubicar.
 */
export function resolveScreen(url: string): ScreenLabel {
  const path =
    String(url ?? '')
      .split('?')[0]
      .split('#')[0] || '/'
  if (path === '/') return SCREEN_LABELS[0]

  const parts = segmentsOf(path)

  for (const entry of SCREEN_LABELS) {
    const pattern = segmentsOf(entry.pattern)
    if (pattern.length !== parts.length) continue
    if (pattern.every((seg, i) => seg.startsWith(':') || seg === parts[i])) return entry
  }

  return { pattern: path, label: UNKNOWN_SCREEN_LABEL }
}
